import { stripeService } from '@/services/stripe';
import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { buildStripeAmazonPayExpressRequest, buildStripeAmazonPayRequest } from '@/utils/amazonPay';
import { isStripePaymentResponse } from '@/types/guards';
import { FinalizeStatus, StripePaymentIntentStatus, type StripePaymentResponse } from '@/types/payment';
import type {
  AmazonPayPaymentParams,
  StripeExpressCheckoutParams,
  StripePaymentElementParams,
} from '@/types/paymentHandlers';

type AmazonPaySettings = NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>;

// PaymentElement flow: user selected the Amazon Pay tile in DEFAULT. The
// billing details come from the checkout context (customer email +
// geo-located country); Stripe's PaymentElement handles the redirect tab to
// Amazon Pay via `<RedirectPayment/>` after this handler returns REDIRECT.
const handleStripePaymentElementAmazonPay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: StripePaymentElementParams,
  amazonPaySettings: AmazonPaySettings
): Promise<StripePaymentResponse> => {
  const { elements } = params;

  const billingDetails = {
    email: checkout.customer.email,
    address: {
      country: checkout.customer.geoLocator.countryCode2,
    },
  };

  const paymentMethod = await stripeService.createAmazonPayPaymentMethod(elements, billingDetails);
  const request = buildStripeAmazonPayRequest(paymentMethod, amazonPaySettings, checkout.customer.email || '');

  return await sendPaymentToServer<StripePaymentResponse>(request);
};

// Express Checkout Element flow: Amazon Pay is rendered as a one-click
// wallet button. Stripe already drove the Amazon auth popup and passes
// `event.billingDetails` back through the ECE `onConfirm` event.
//
// Unlike the PaymentElement path, we CANNOT tokenize into a Stripe
// PaymentMethod client-side here: Stripe's ECE probe drops redirect
// wallets (Amazon Pay is one) from `availablePaymentMethods` whenever
// the parent `<Elements>` group has `paymentMethodCreation: 'manual'`,
// which `stripe.createPaymentMethod({ elements })` requires. Setting the
// flag hides the button; leaving it off makes the call throw.
//
// So we mirror the BLIK deferred-intent flow instead:
//   1. Ask the BE to mint an unconfirmed `PaymentIntent` for `amazon_pay`
//      and hand back its `client_secret`.
//   2. Confirm client-side via `stripe.confirmPayment({ elements,
//      clientSecret, redirect: 'if_required' })`, letting Stripe attach
//      the Amazon session it already holds.
//   3. Fold Stripe's `paymentIntent.status` back into the finalize
//      envelope so downstream handlers see the same shape they do for
//      BLIK / card 3DS.
const handleStripeExpressCheckoutAmazonPay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: StripeExpressCheckoutParams,
  amazonPaySettings: AmazonPaySettings
): Promise<StripePaymentResponse> => {
  const { event, elements, stripe } = params;
  const receiptEmail = event.billingDetails?.email || checkout.customer.email || '';
  const finalizeRequest = buildStripeAmazonPayExpressRequest(amazonPaySettings, receiptEmail);
  const finalizeResponse = await sendPaymentToServer<StripePaymentResponse>(finalizeRequest);

  if (!isStripePaymentResponse(finalizeResponse) || !finalizeResponse.stripe?.clientSecret) {
    return finalizeResponse;
  }

  // `stripe` is the instance that owns `elements` — see
  // `StripeExpressCheckoutParams.stripe` for why the shared slot is not enough.
  const { paymentIntent, error } = await stripeService.confirmAmazonPayExpressPayment(
    elements,
    finalizeResponse.stripe.clientSecret,
    stripe
  );

  if (error) {
    return {
      ...finalizeResponse,
      stripe: { ...finalizeResponse.stripe, status: FinalizeStatus.FAILED },
      appChargeErrorType: stripeService.mapStripeErrorCode(
        error.code ?? error.payment_intent?.last_payment_error?.code
      ),
    } satisfies StripePaymentResponse;
  }

  if (paymentIntent?.status === StripePaymentIntentStatus.SUCCEEDED) {
    return {
      ...finalizeResponse,
      stripe: { ...finalizeResponse.stripe, status: FinalizeStatus.SUCCESS, intentId: paymentIntent.id },
    } satisfies StripePaymentResponse;
  }

  return {
    ...finalizeResponse,
    stripe: {
      ...finalizeResponse.stripe,
      status: FinalizeStatus.FAILED,
      intentId: paymentIntent?.id ?? finalizeResponse.stripe.intentId,
    },
    appChargeErrorType: stripeService.mapStripeErrorCode(paymentIntent?.last_payment_error?.code),
  } satisfies StripePaymentResponse;
};

const isExpressCheckoutParams = (params: AmazonPayPaymentParams): params is StripeExpressCheckoutParams =>
  'event' in params && params.event !== undefined;

const handleStripeAmazonPay = (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: AmazonPayPaymentParams,
  amazonPaySettings: AmazonPaySettings
): Promise<StripePaymentResponse> => {
  if (isExpressCheckoutParams(params)) {
    return handleStripeExpressCheckoutAmazonPay(checkout, sendPaymentToServer, params, amazonPaySettings);
  }
  return handleStripePaymentElementAmazonPay(checkout, sendPaymentToServer, params, amazonPaySettings);
};

export const handleAmazonPayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params?: AmazonPayPaymentParams
): Promise<StripePaymentResponse> => {
  if (!params) {
    throw new Error('Amazon Pay payment requires parameters');
  }

  const amazonPaySettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.AMAZON_PAY
  );

  if (!amazonPaySettings) {
    throw new Error('Amazon Pay settings not found');
  }

  switch (amazonPaySettings.provider) {
    case ProviderEnum.STRIPE:
      return await handleStripeAmazonPay(checkout, sendPaymentToServer, params, amazonPaySettings);
    default:
      throw new Error(`Unsupported provider for Amazon Pay: ${amazonPaySettings.provider}`);
  }
};
